/** One line in the chat about bot tasks, instead of the task list: a task
 * waiting for the person's approval, or one that settled while the chat was
 * open. View opens the Bots view, where the task can be acted on. */
import { useState } from 'react';
import { useBotTasks } from '../workspaces/bots/useBotTasks';
import { isBotTaskActive, type BotTask } from '../../services/BotTasksService';

const SETTLED: Partial<Record<BotTask['status'], string>> = { completed: 'finished', failed: 'could not finish', cancelled: 'was stopped' };

const shortPrompt = (prompt: string) => (prompt.length > 60 ? `${prompt.slice(0, 57).trimEnd()}...` : prompt);

/** Exported for tests: the line to show, approvals first because they block
 * the bot until the person acts. `seenActive` holds the tasks this chat saw
 * moving, so a task that finished before it opened is not announced. */
export function botTaskNotice(tasks: BotTask[], seenActive: ReadonlySet<string>): string | null {
  const waiting = tasks.find((task) => task.status === 'needs_approval');
  if (waiting) return `${waiting.botName ?? 'A bot'} needs your approval: ${shortPrompt(waiting.prompt)}`;
  const settled = tasks.find((task) => seenActive.has(task.id) && SETTLED[task.status]);
  if (settled) return `${settled.botName ?? 'A bot'} ${SETTLED[settled.status]}: ${shortPrompt(settled.prompt)}`;
  return null;
}

export function SIBotTaskNotice({ workspaceId, refreshKey, onView }: { workspaceId: string; refreshKey: string | number; onView: () => void }) {
  const { tasks } = useBotTasks(workspaceId, undefined, refreshKey);
  const [seenActive, setSeenActive] = useState<ReadonlySet<string>>(() => new Set());
  const moving = tasks.filter((task) => isBotTaskActive(task) && !seenActive.has(task.id));
  if (moving.length) setSeenActive(new Set([...seenActive, ...moving.map((task) => task.id)]));
  const line = botTaskNotice(tasks, seenActive);
  if (!line) return null;
  return (
    <p role="status" className="mb-4 flex min-w-0 items-baseline gap-2 text-xs text-muted-foreground">
      <span className="min-w-0 flex-1 truncate">{line}</span>
      <button type="button" className="shrink-0 font-medium text-foreground underline-offset-2 hover:underline" onClick={onView}>View</button>
    </p>
  );
}
